import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Button } from 'primereact/button';
import { Toast } from 'primereact/toast';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { InputText } from 'primereact/inputtext';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Link } from 'react-router-dom';

import { StorageService } from '../../service/StorageService';
import { translatedMessage } from '../../service/LanguageService';
import DataTableUtils from '../../utilities/DataTableUtils';
import StorageFileDownloadTableButton from './StorageFileDownloadTableButton';
import StorageFileViewTableButton from './StorageFileViewTableButton';
import DeleteModalComponent from '../DeleteModalComponent';
import EnumService from '../../service/EnumService';
import GeneralUtils from '../../utilities/GeneralUtils';
import { Message } from 'primereact/message';
import StorageFileCustomUpload from './StorageFileCustomUpload';
import ValidationErrorModalComponent from '../ValidationErrorModalComponent';
import CompetitionUtils from '../../utilities/CompetitionUtils';

const CompetitionStorageFolderFileList = (props) => {
    const [competition, setCompetition] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [fileList, setFileList] = useState(null);
    const [globalFilter, setGlobalFilter] = useState(null);
    const [selectedValue, setSelectedValue] = useState(null);
    const [showDeleteDialog, setShowDeleteDialog] = useState(false);
    const [showRPUploadDialog, setShowRPUploadDialog] = useState(false);
    const [usageLookup, setUsageLookup] = useState([]);
    const [addUsageOnUpload, setAddUsageOnUpload] = useState(false)
    const [showValidationErrorDialog, setShowValidationErrorDialog] = useState(false);
    const [validationErrors, setValidationErrors] = useState([]);

    const toast = useRef(null);
    const dt = useRef(null);

    const storageService = useMemo(() => new StorageService(), []);

    const getLabelForValue = (value, array) => {
        const item = array.filter(x => x.value === value)
        return item[0] ? item[0].label : value
    }

    useEffect(() => {
        setIsLoading(true)
        setCompetition(props?.competition)

        const getFiles = async () => {
            let usageLookupResponse = await EnumService.getEntityLookupByField("NOM:FILE_USAGE");
            setUsageLookup(usageLookupResponse)

            await storageService.getFilesInFolder(props.competition.folder.id)
                .then(filesResponse => {
                    // console.log(filesResponse)
                    let appFiles = []
                    filesResponse.forEach(item => {
                            item.usageLabel = item.usage ? getLabelForValue(item.usage.id, usageLookupResponse) : null

                            appFiles.push(item)
                    })

                    setFileList(appFiles)
                    setIsLoading(false)
                })
                .catch(error => toast?.current?.show({ severity: 'error', summary: translatedMessage(error), life: 5000 }))
        }

        if (props.competition.folder.id) {
            getFiles();
        } else {
            setIsLoading(false)
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props, storageService]);

    const header = (
        <div className="flex flex-column md:flex-row md:justify-content-between md:align-items-center">
            <div className="mt-2 md:mt-0 mr-2">
                <Button label={translatedMessage("file.downloadAll")} icon="pi pi-download" className="p-button-primary mr-2 my-1 md:my-1 white-space-nowrap"
                    onClick={() => handleDownloadAll()} disabled={fileList && fileList.length === 0}
                />
            </div>

            <div className="mt-2 md:mt-0 p-input-icon-left flex justify-content-end">
                <div className="p-input-icon-left w-auto">
                    <i className="pi pi-search" />
                    <InputText type="search" onInput={(e) => setGlobalFilter(e.target.value)} placeholder={translatedMessage("generic.search")}
                        className="my-1 md:my-1" />
                </div>
            </div>
        </div>
    );

    const doOnFilter = (data) => { }

    const actionBodyTemplate = (rowData) => {
        return (
            <div className="actions flex flex-wrap align-items-center justify-content-end">
                {<StorageFileViewTableButton fileUuid={rowData.uuid} fileName={rowData.name} />}
                {<StorageFileDownloadTableButton fileUuid={rowData.uuid} fileName={rowData.name} />}
                <Button icon="pi pi-upload" className="p-button-rounded p-button-info m-1"
                    onClick={() => handleCustomUpload(rowData, false)}
                    tooltip={translatedMessage("generic.file.upload")} tooltipOptions={{ showOnDisabled: true, position: 'left' }}
                    disabled={!CompetitionUtils.canChangeFiles(competition, rowData.usage.code)}
                />
                <Button icon="pi pi-trash" className="p-button-rounded p-button-danger m-1" onClick={() => handleDelete(rowData)}
                    tooltip={translatedMessage("generic.delete")}
                    tooltipOptions={{ position: 'top' }}
                    visible={CompetitionUtils.canChangeFiles(competition) && false}
                />
            </div>
        );
    };

    const usageBodyTemplate = (rowData) => {
        return (
            <div title={translatedMessage("file.usage.select")}>
                <span className='mr-2'>{rowData.usageLabel ? rowData.usageLabel : "-"}</span>
            </div>
        )
    }

    // const sizeBodyTemplate = (rowData) => {
    //     let roundedSize = (rowData.size / (1024 * 1024)).toFixed(2)
    //     return roundedSize.toString().concat(" MB");
    // }

    const dateBodyTemplate = (_date) => {
        return GeneralUtils.formatDateTime(_date);
    }

    const handleDownloadAll = () => {
        if (competition.folder.id) {
            storageService.downloadFilesInFolder(competition.folder.id, competition.folder.name.replaceAll(' ', '_').toLowerCase())
                .catch((errorMessage) => toast.current.show({ severity: 'error', summary: errorMessage, life: 5000 }));
        }
    }

    const handleDelete = (data) => {
        setSelectedValue(data)
        setShowDeleteDialog(true)
    }

    const deleteRecord = () => {
        storageService.deleteFile(selectedValue.uuid)
            .then(() => {
                let _fileList = fileList.filter(item => item.id !== selectedValue.id)
                setFileList(_fileList)
                setShowDeleteDialog(false);

                toast.current.show({ severity: 'success', summary: translatedMessage("generic.delete.success") });
            })
            .catch(error => {
                toast.current.show({ severity: 'error', summary: translatedMessage(error), life: 5000 });
            })
    };

    const handleCustomUpload = (data, _addUsageOnUpload = false) => {
        setSelectedValue(data)
        setAddUsageOnUpload(_addUsageOnUpload)
        setShowRPUploadDialog(true)
    };

    const onFileUploadSuccess = (id, response) => {
        let _fileList = fileList.map(item => ({ ...item }))
        if (response?.file) {
            response.file.usageLabel = response?.file.usage?.label
            _fileList.push(response.file)
        }

        if (typeof props.afterFileUpload === 'function') {
            props.afterFileUpload(response?.competition);
        }

        setFileList(_fileList)
        toast.current.show({ severity: 'success', summary: translatedMessage('generic.file.upload.success') });
    };

    const onFileUploadError = (error) => {
        if (error?.hasValidationError) {
            setValidationErrors(error.errorCodes)
            setShowValidationErrorDialog(true)
        } else {
            error && toast?.current?.show({ severity: 'error', summary: translatedMessage(error.errorMessage), life: 7000 })
        }

    };

    if (isLoading) {
        return (
            <div className='w-full flex align-items-center'>
                <Toast ref={toast} />
                <ProgressSpinner />
            </div>
        );
    } else {
        let infoText = props.pageSubTitle ? props.pageSubTitle : null;
        if (props.pageInfo) {
            infoText += " " + props.pageInfo;
        }

        return (
            <>
                <Toast ref={toast} />

                {props.pageTitle &&
                    <div className='w-full text-align-left'>
                        <h5 className='mb-2'>{props.pageTitle}</h5>
                    </div>
                }
                {infoText &&
                    <Message severity="warn" text={infoText} />
                }
                {props.pageInfoLink && props.pageInfoLinkText &&
                    <div className='w-full text-align-left mt-2'>
                        {props.pageInfoLinkText + " "}
                        <Link to={props.pageInfoLink}>
                            <span className='font-bold underline'>{translatedMessage('generic.here').toString().toUpperCase() + "."}</span>
                        </Link>
                    </div>
                }
                {props.pageInfoAddFiles &&
                    <div className='w-full text-align-left mt-2'>
                        {props.pageInfoAddFiles}
                    </div>
                }
                <DataTable
                    ref={dt}
                    value={fileList}
                    dataKey="id"
                    paginator={props?.hidePaginator ? false : true}
                    rows={DataTableUtils.defalRowsPerPage()}
                    rowsPerPageOptions={props?.hidePaginator ? 25 : DataTableUtils.rowsPerPageOptions()}
                    className="datatable-responsive pcn-datatable"
                    paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                    currentPageReportTemplate={"{first} - {last} " + translatedMessage('generic.of') + " {totalRecords}"}
                    globalFilter={globalFilter}
                    emptyMessage={translatedMessage("generic.tableEmptyMessage")}
                    header={header}
                    responsiveLayout="stack"
                    onFilter={doOnFilter}
                    metaKeySelection={true}
                    sortMode="multiple"
                    multiSortMeta ={[{ field: 'usageLabel', order: 1 }, { field: 'uploadedOn', order: -1 }]}
                    removableSort
                >
                    <Column
                        field="usageLabel"
                        header={translatedMessage("file.usage")}
                        sortable
                        headerStyle={{ width: '30%', minWidth: '8rem' }}
                        body={usageBodyTemplate}
                    />
                    <Column
                        field="fileDate"
                        header={translatedMessage("file.generation.date")}
                        sortable
                        headerStyle={{ width: '12%', minWidth: '8rem' }}
                        body={(e) => dateBodyTemplate(e.fileDate)}
                    />
                    <Column
                        field="name"
                        header={translatedMessage("generic.nameRo")}
                        sortable
                        headerStyle={{ width: '30%', minWidth: '8rem' }}
                    />
                    <Column
                        field="uploadedOn"
                        header={translatedMessage("file.upload.date")}
                        sortable
                        headerStyle={{ width: '12%', minWidth: '8rem' }}
                        body={(e) => dateBodyTemplate(e.uploadedOn)}
                    />
                    {/* <Column
                        field="size"
                        header={translatedMessage("file.size")}
                        sortable
                        headerStyle={{ width: '10%', minWidth: '8rem' }}
                        body={sizeBodyTemplate}
                    /> */}
                    <Column body={actionBodyTemplate}></Column>
                </DataTable>

                {showRPUploadDialog &&
                    <StorageFileCustomUpload
                        entityId={competition.id}
                        endPoint={"/competition/job/send"}
                        folderId={competition.folder.id}
                        usageId={selectedValue?.usage?.id}
                        deletePrevious={false}
                        multiple={false}
                        accept={["pdf"]}
                        visible={showRPUploadDialog}
                        visibleSetter={setShowRPUploadDialog}
                        onUploadSuccess={onFileUploadSuccess}
                        onUploadError={onFileUploadError}
                        usageLookup={usageLookup}
                        addUsageOnUpload={addUsageOnUpload}
                    />
                }

                {showDeleteDialog &&
                    <DeleteModalComponent
                        visible={showDeleteDialog}
                        item={selectedValue && selectedValue.name}
                        closeDialog={() => setShowDeleteDialog(false)}
                        deleteRecord={() => deleteRecord()}
                    />
                }

                {showValidationErrorDialog &&
                    <ValidationErrorModalComponent
                        visible={showValidationErrorDialog}
                        errors={validationErrors}
                        dialogTitle={translatedMessage("recruitment.plan.file.upload.title")}
                        dialogInfo={translatedMessage("recruitment.plan.file.upload.info")}
                        closeDialog={() => setShowValidationErrorDialog(false)}
                    />
                }
            </>
        );
    }
};

export default CompetitionStorageFolderFileList;